/**
 * Account → Profile and Account → Plan (web.md §4).
 *
 * Profile: name, the signed-in email (read-only), time zone, language, notifications — the synced
 * `settings.profile`. Plan: the current plan card (the prototype's `.account-plan-card`), usage against
 * every limit (`plan.usage()` vs `plan.entitlement().limits`, plus the server counters of
 * `GET /api/account` when signed in) and "Change plan" → /subscribe.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { AccountResponse, DigesterError, Entitlement, MeResponse, PlanLimits, ProfileSettings, SyncedSettings, Usage } from '@digester/core';
import { planLabel } from '../../app/hooks.js';
import { useServices } from '../../app/services.js';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { formatExact } from '../../ui/format.js';
import { Field, Section, Select, Toggle } from '../../ui/form.js';
import { openPasswordDialog } from '../../account/Password.js';
import { formatDate, periodLabel, useAccountState } from '../../account/shared.js';
import { EditorFrame, useDraft } from '../shell/index.js';

/**
 * Account → Profile → Password: "Set" or "Not set", from the server (`GET /api/auth/me`), with Set or
 * Change in a dialog (account/Password.tsx). Signing in itself lives on /signin.
 */
function PasswordSection({ email }: { email: string }) {
  const { api } = useServices();
  const me = useQuery<MeResponse, DigesterError>({ queryKey: ['account', 'me'], queryFn: () => api.call<MeResponse>('me') });
  const has = me.data?.hasPassword;
  return (
    <Section title="Password" help="Sign in with your e-mail and password on any browser or device; a code by e-mail is only for the first time or a forgotten password.">
      <div className="read-only-row" data-password-state={has === undefined ? 'unknown' : has ? 'set' : 'not-set'}>
        <span>Password</span>
        <strong>{me.isError ? 'Could not check' : has === undefined ? '…' : has ? 'Set' : 'Not set'}</strong>
      </div>
      <div className="panel-actions">
        <button type="button" className="secondary-button" disabled={has === undefined} onClick={() => openPasswordDialog(email, has === true)}>
          <Icon name="key-round" /> {has ? 'Change password' : 'Set a password'}
        </button>
      </div>
    </Section>
  );
}

const TIME_ZONES = ['Europe/Belgrade', 'Europe/London', 'America/New_York', 'Asia/Tokyo', 'UTC'];

function timeZoneOptions(current: string): string[] {
  const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return [...new Set([current, browser, ...TIME_ZONES].filter((z): z is string => typeof z === 'string' && z !== ''))];
}

export function ProfileEditor({ settings }: { settings: SyncedSettings }) {
  const engine = useEngine();
  const queryClient = useQueryClient();
  const account = useAccountState();
  const navigate = useNavigate();
  const draft = useDraft<ProfileSettings>({
    initial: settings.profile,
    onSave: async (value) => {
      queryClient.setQueryData(qk.settings(), await engine.settings.set('profile', { ...value, name: value.name.trim() }));
    },
  });
  const v = draft.value;
  return (
    <EditorFrame title="Profile" icon="user">
      <Section title="Profile">
        <Field label="Name" name="name" value={v.name} required maxLength={80} onChange={(name) => draft.set((d) => ({ ...d, name }))} />
        <Field label="Email" name="email" type="email" value={account.email ?? ''} readOnly placeholder="Not signed in" />
        {account.signedIn ? null : (
          <button type="button" className="text-link profile-signin" onClick={() => navigate('/signin?next=/settings/account/profile')}>
            <Icon name="log-in" /> Sign in to sync and subscribe
          </button>
        )}
        <Select label="Time zone" name="timezone" value={v.timezone} options={timeZoneOptions(v.timezone)} onChange={(timezone) => draft.set((d) => ({ ...d, timezone }))} />
        <Select<ProfileSettings['language']>
          label="Language"
          name="language"
          value={v.language}
          options={[
            ['en', 'English'],
            ['ru', 'Русский'],
            ['sr', 'Српски'],
          ]}
          onChange={(language) => draft.set((d) => ({ ...d, language }))}
        />
        <Toggle
          label="Notify on new priority messages"
          name="notifications"
          checked={v.notifications}
          help="Notifications arrive with the mobile apps; the web app stores the choice."
          onChange={(notifications) => draft.set((d) => ({ ...d, notifications }))}
        />
      </Section>
      {account.signedIn && account.email ? <PasswordSection email={account.email} /> : null}
    </EditorFrame>
  );
}

type UsageRow = { key: string; label: string; used: number | null; max: number | null; included?: boolean };

function usageRows(usage: Usage, limits: PlanLimits, server: AccountResponse['usage'] | null): UsageRow[] {
  return [
    { key: 'sources', label: 'Sources', used: usage.sources, max: limits.sources },
    { key: 'messages', label: 'New messages today', used: usage.messagesToday, max: limits.messagesPerDay },
    { key: 'filters', label: 'Filters', used: usage.filters, max: limits.filters },
    { key: 'topics', label: 'Topics', used: usage.topics, max: limits.topics },
    { key: 'tags', label: 'Tags', used: usage.tags, max: limits.tags },
    { key: 'folders', label: 'Folders', used: usage.folders, max: limits.folders },
    { key: 'views', label: 'Saved views', used: usage.views, max: limits.views },
    { key: 'scoring-rules', label: 'Scoring rules', used: usage.scoringRules, max: limits.scoringRules },
    { key: 'scored', label: 'Messages scored today', used: server?.scoredToday ?? usage.scoredToday, max: null, included: limits.aiScoring },
    { key: 'devices', label: 'Synced devices', used: server?.syncDevices ?? usage.syncDevices, max: limits.syncDevices },
    { key: 'connectors', label: 'Assistant connections', used: server?.connectors ?? usage.connectors, max: limits.connectors },
    { key: 'requests', label: 'Assistant requests today', used: server?.connectorRequestsToday ?? usage.connectorRequestsToday, max: limits.connectorRequestsPerDay },
  ];
}

function UsageValue({ row }: { row: UsageRow }) {
  if (row.included === false) return <>Not included</>;
  if (row.max === 0) return <>Not included</>;
  const used = row.used === null ? '—' : formatExact(row.used);
  if (row.max === null) return <>{used}</>;
  const ratio = row.used === null ? 0 : Math.min(1, row.used / row.max);
  return (
    <span className="usage-value">
      <span className={ratio >= 1 ? 'usage-meter full' : 'usage-meter'} aria-hidden="true">
        <i style={{ width: `${Math.round(ratio * 100)}%` }} />
      </span>
      {used} / {formatExact(row.max)}
    </span>
  );
}

export function PlanEditor({ entitlement }: { entitlement: Entitlement }) {
  const navigate = useNavigate();
  const { api } = useServices();
  const account = useAccountState();
  const usage = useEngineQuery(qk.plan('usage'), (e) => e.plan.usage(), { refetchOnMount: 'always' }).data;
  const server = useQuery<AccountResponse, DigesterError>({ queryKey: ['account', 'server'], queryFn: () => api.call<AccountResponse>('account'), enabled: account.signedIn });
  const pro = entitlement.plan === 'pro';
  const active = !pro || entitlement.validUntil === null || entitlement.validUntil > Date.now();
  return (
    <EditorFrame title="Plan" icon="badge-check">
      <Section title="Subscription">
        <div className="account-plan-card" data-plan={planLabel(entitlement)}>
          <div>
            <span className="plan-emblem">
              <Icon name="badge-check" />
            </span>
            <div>
              <h2>{planLabel(entitlement)}</h2>
              <p>Personal plan</p>
            </div>
            <span className={active ? 'status-label enabled' : 'status-label'}>{active ? 'Active' : 'Ended'}</span>
          </div>
          <dl>
            <div>
              <dt>Billing</dt>
              <dd>{pro ? periodLabel(entitlement.period) : 'Free'}</dd>
            </div>
            {pro ? (
              <div>
                <dt>Renews</dt>
                <dd>{formatDate(entitlement.validUntil)}</dd>
              </div>
            ) : null}
            <div>
              <dt>Account</dt>
              <dd>{account.email ?? 'Not signed in'}</dd>
            </div>
          </dl>
        </div>
        <div className="panel-actions">
          <button type="button" className="primary-button" onClick={() => navigate('/subscribe')}>
            {pro ? 'Change plan' : 'Upgrade to Pro'}
          </button>
        </div>
        <p className="field-help">Payments run in test mode in this build: nothing is charged.</p>
      </Section>
      <Section title="Usage" help="Against the limits of your plan.">
        {usage ? (
          usageRows(usage, entitlement.limits, server.data?.usage ?? null).map((row) => (
            <div className="read-only-row" key={row.key} data-usage={row.key}>
              <span>{row.label}</span>
              <strong>
                <UsageValue row={row} />
              </strong>
            </div>
          ))
        ) : (
          <p className="field-help">Loading…</p>
        )}
      </Section>
    </EditorFrame>
  );
}
